/**
 * WastewaterTimeSeriesChart
 *
 * Line chart of annual wastewater GHG emissions.
 * All years form ONE continuous line; the 2020 measured point is visually
 * distinguished with a larger, filled marker and appears in the legend separately.
 */

import React, { useCallback, useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';
import type { WastewaterTimeSeriesPoint } from '../../services/wastewaterAnalyticsService';

Chart.register(...registerables);

interface WastewaterTimeSeriesChartProps {
  points: WastewaterTimeSeriesPoint[];
  location: string;
  showPerCapita?: boolean;
}

const WASTEWATER_COLOR = '#0ea5e9';
const MEASURED_COLOR = '#0369a1'; // darker blue for measured point

const WastewaterTimeSeriesChart: React.FC<WastewaterTimeSeriesChartProps> = ({
  points,
  location,
  showPerCapita = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);

  const destroyChart = useCallback(() => {
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }
    if (canvasRef.current) {
      try {
        const existing = Chart.getChart(canvasRef.current);
        if (existing) existing.destroy();
      } catch {}
    }
  }, []);

  useEffect(() => {
    if (!canvasRef.current || points.length === 0) return;
    destroyChart();

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    const dataKey = showPerCapita ? 'per_capita' : 'emissions_mtco2e';
    const labels = points.map((p) => String(p.year));
    const allValues = points.map((p) => (p[dataKey] != null ? Number(p[dataKey]) : null));

    // Point styling arrays: measured years get bigger, filled circles
    const pointRadii = points.map((p) => (p.isEstimated ? 3 : 6));
    const pointBgColors = points.map((p) =>
      p.isEstimated ? 'rgba(14,165,233,0.4)' : MEASURED_COLOR
    );
    const pointBorderColors = points.map((p) =>
      p.isEstimated ? WASTEWATER_COLOR : MEASURED_COLOR
    );
    const pointBorderWidths = points.map((p) => (p.isEstimated ? 1 : 2.5));

    // Highlight dataset: only the measured year(s) — rendered as scatter overlay for legend
    const measuredOverlay = points.map((p) =>
      !p.isEstimated && p[dataKey] != null ? Number(p[dataKey]) : null
    );

    const yLabel = showPerCapita ? 'MTCO₂e per capita' : 'MTCO₂e';
    const title = showPerCapita
      ? `Wastewater Emissions Per Capita — ${location}`
      : `Wastewater GHG Emissions Through Time — ${location}`;

    chartRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Population-scaled estimate',
            data: allValues,
            borderColor: WASTEWATER_COLOR,
            backgroundColor: 'rgba(14,165,233,0.08)',
            borderWidth: 2,
            borderDash: [6, 4],
            pointRadius: pointRadii,
            pointHoverRadius: pointRadii.map((r) => r + 2),
            pointBackgroundColor: pointBgColors,
            pointBorderColor: pointBorderColors,
            pointBorderWidth: pointBorderWidths,
            fill: true,
            spanGaps: true,
            tension: 0.25,
          },
          {
            label: 'Measured (2020 inventory)',
            data: measuredOverlay,
            type: 'scatter',
            pointRadius: 7,
            pointHoverRadius: 9,
            pointBackgroundColor: MEASURED_COLOR,
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            showLine: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          title: {
            display: true,
            text: title,
            font: { size: 14, weight: 'bold' },
            color: '#1f2937',
            padding: { bottom: 12 },
          },
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              boxWidth: 14,
              font: { size: 12 },
              color: '#374151',
              filter: (item) => item.datasetIndex !== 0 || item.text === 'Population-scaled estimate',
            },
          },
          tooltip: {
            callbacks: {
              title: (items) => `${items[0]?.label ?? ''}`,
              label: (ctx) => {
                if (ctx.datasetIndex === 1) return ''; // skip duplicate from scatter
                const raw = ctx.raw as number | null;
                if (raw == null) return '';
                const year = Number(ctx.label ?? 0);
                const pt = points.find((p) => p.year === year);
                const formatted = showPerCapita
                  ? raw.toLocaleString('en-US', { maximumFractionDigits: 5 })
                  : raw.toLocaleString('en-US', { maximumFractionDigits: 0 });
                const source = pt?.isEstimated
                  ? ' (population-scaled estimate)'
                  : ' (measured)';
                return `${formatted} ${yLabel}${source}`;
              },
              footer: (items) => {
                const year = Number(items[0]?.label ?? 0);
                const pt = points.find((p) => p.year === year);
                return pt?.population
                  ? `Population: ${pt.population.toLocaleString()}`
                  : '';
              },
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: '#6b7280', font: { size: 11 } },
          },
          y: {
            min: 0,
            grid: { color: 'rgba(0,0,0,0.06)' },
            ticks: {
              color: '#6b7280',
              font: { size: 11 },
              callback: (v) =>
                showPerCapita
                  ? Number(v).toLocaleString('en-US', { maximumFractionDigits: 5 })
                  : Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 }),
            },
            title: {
              display: true,
              text: yLabel,
              color: '#6b7280',
              font: { size: 11 },
            },
          },
        },
      },
    });

    return () => destroyChart();
  }, [points, location, showPerCapita, destroyChart]);

  if (points.length === 0) {
    return (
      <div style={{ padding: 32, color: '#6b7280', textAlign: 'center' }}>
        Population data not available for this jurisdiction — time series cannot be generated.
      </div>
    );
  }

  const measuredYears = points.filter((p) => !p.isEstimated).map((p) => p.year);

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <canvas ref={canvasRef} />
      <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 6, textAlign: 'center' }}>
        {measuredYears.length > 0
          ? `Solid point${measuredYears.length > 1 ? 's' : ''} = measured inventory (${measuredYears.join(', ')}). Dashed line = population-scaled estimates.`
          : 'All years are population-scaled estimates from the 2020 baseline.'}
      </p>
    </div>
  );
};

export default WastewaterTimeSeriesChart;
